'use client'

import { type ReactNode } from 'react'
import { Checkbox } from '@ui/base/checkbox'
import { Field, FieldError, FieldLabel } from '@ui/base/field'
import { type AuthDto } from '@view/auth/types'
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form'

import { type AuthFormValues } from './form-types'

const consentNames = ['terms', 'personalData', 'promo'] as const

interface ConsentCheckboxesProps {
  control: Control<AuthFormValues>
  setValue: UseFormSetValue<AuthFormValues>
  errors: {
    terms?: string
    personalData?: string
  }
}

interface ConsentRowProps {
  id: keyof AuthDto.Consents
  control: Control<AuthFormValues>
  error?: string
  children: ReactNode
}

const ConsentRow = ({ id, control, error, children }: ConsentRowProps) => {
  const errorId = `${id}-error`
  const labelId = `${id}-label`

  return (
    <div>
      <Field orientation="horizontal" className="items-start gap-2" data-invalid={Boolean(error)}>
        <Controller
          control={control}
          name={id}
          render={({ field }) => (
            <Checkbox
              id={id}
              className="mt-0.5"
              checked={field.value}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
              // Базовый чекбокс — не тег input, поэтому связь с подписью задаётся явно.
              aria-labelledby={labelId}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
            />
          )}
        />
        {/* block вместо flex: подпись содержит ссылки, они должны идти обычным текстом. */}
        <FieldLabel id={labelId} htmlFor={id} className="block text-xs leading-snug font-normal text-gray-600">
          {children}
        </FieldLabel>
      </Field>
      {error && (
        <FieldError id={errorId} className="mt-0.5 pl-6">
          {error}
        </FieldError>
      )}
    </div>
  )
}

export const ConsentCheckboxes = ({ control, setValue, errors }: ConsentCheckboxesProps) => {
  const givenConsents = useWatch({ control, name: consentNames }).filter(Boolean).length
  const allGiven = givenConsents === consentNames.length

  const toggleAll = (isChecked: boolean) => {
    for (const name of consentNames) {
      setValue(name, isChecked, { shouldValidate: true })
    }
  }

  return (
    <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left">
      <Field orientation="horizontal" className="items-start gap-2">
        <Checkbox
          id="consent-all"
          className="mt-0.5"
          checked={allGiven}
          // Часть галок проставлена: общая показывает промежуточное состояние, а не «согласен со всем».
          indeterminate={givenConsents > 0 && !allGiven}
          onCheckedChange={toggleAll}
          aria-labelledby="consent-all-label"
        />
        <FieldLabel
          id="consent-all-label"
          htmlFor="consent-all"
          className="block text-xs leading-snug font-medium text-gray-700"
        >
          Согласен со всем перечисленным
        </FieldLabel>
      </Field>

      <div className="mt-2 space-y-1.5 border-t border-gray-200 pt-2">
        <ConsentRow id="terms" control={control} error={errors.terms}>
          Принимаю{' '}
          <a href="/oferta" target="_blank" rel="noreferrer" className="link">
            Пользовательское соглашение
          </a>
        </ConsentRow>

        <ConsentRow id="personalData" control={control} error={errors.personalData}>
          Даю{' '}
          <a href="/assets/docs/policy.pdf" target="_blank" rel="noreferrer" className="link">
            согласие на обработку персональных данных
          </a>{' '}
          и ознакомлен(а) с{' '}
          <a href="/assets/docs/policy.pdf" target="_blank" rel="noreferrer" className="link">
            Политикой конфиденциальности
          </a>
        </ConsentRow>

        <ConsentRow id="promo" control={control}>
          Согласен(на) получать рекламные материалы
        </ConsentRow>
      </div>
    </div>
  )
}
